/**
 * Admin V5 — Upcoming Sessions operational overview (Phase 14 Session 1).
 * Shows student-local, tutor-local, and admin-local times for each upcoming
 * session across Learning Plans V2 + Course Bookings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCcw, Copy, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'week', label: 'This Week' },
  { key: 'upcoming', label: 'Upcoming' },
];

const AdminUpcomingSessionsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState('week');
  const [tutorFilter, setTutorFilter] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [adminTz, setAdminTz] = useState({ tz: '', confirmed: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { window, limit: 200 };
      if (tutorFilter) params.tutor = tutorFilter;
      if (studentFilter) params.student = studentFilter;
      const r = await ax.get('/api/admin/upcoming-sessions', { params });
      setItems(r.data?.items || []);
      setAdminTz({ tz: r.data?.admin_tz, confirmed: r.data?.admin_tz_confirmed });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [window, tutorFilter, studentFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="admin-upcoming-sessions">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-4 w-4 text-red-700" /> Upcoming Sessions
          </CardTitle>
          <div className="text-[10px] text-gray-500 mt-1">
            Admin timezone: <span className="font-mono">{adminTz.tz || 'Australia/Melbourne'}</span>
            {!adminTz.confirmed && <span className="ml-1 text-amber-700">· not confirmed</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {WINDOWS.map((w) => (
            <button key={w.key} onClick={() => setWindow(w.key)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${window === w.key ? 'bg-red-700 border-red-700 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid={`admin-upcoming-window-${w.key}`}>
              {w.label}
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={load}><RefreshCcw className="h-3.5 w-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Filter by tutor email" value={tutorFilter} onChange={(e) => setTutorFilter(e.target.value)} data-testid="admin-upcoming-tutor-filter" />
          <Input placeholder="Filter by student email" value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)} data-testid="admin-upcoming-student-filter" />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs italic text-gray-400 py-6 text-center border border-dashed rounded-lg">
            No sessions in this window.
          </div>
        ) : (
          <div className="space-y-1.5">
            {items.map((it) => <AdminRow key={it.session_id} it={it} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminRow = ({ it }) => {
  const s = it.student_display || {}; const t = it.tutor_display || {}; const a = it.admin_display || {};
  return (
    <div className="border border-gray-200 rounded-lg p-2.5 bg-white text-xs" data-testid={`admin-upcoming-row-${it.session_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-gray-900">{it.course_label || it.module_name}</div>
          <div className="text-gray-600 mt-0.5">
            Student <span className="font-mono text-gray-500">{it.student_email || '—'}</span>
            · Tutor <span className="font-mono text-gray-500">{it.tutor_email || '—'}</span>
          </div>
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-1 text-[11px]">
            <div>Student: <strong>{s.date} {s.hhmm_ampm}</strong> <span className="text-gray-500">{s.tz_abbrev || s.tz_name}</span> {!s.tz_confirmed && <span className="text-amber-700">·⚠</span>}</div>
            <div>Tutor: <strong>{t.date} {t.hhmm_ampm}</strong> <span className="text-gray-500">{t.tz_abbrev || t.tz_name}</span> {!t.tz_confirmed && <span className="text-amber-700">·⚠</span>}</div>
            <div>Admin: <strong>{a.date} {a.hhmm_ampm}</strong> <span className="text-gray-500">{a.tz_abbrev || a.tz_name}</span></div>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]">{it.status}</Badge>
      </div>
    </div>
  );
};

export default AdminUpcomingSessionsPanel;
